import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, Package, Calendar, Users, ShoppingBag, Inbox, FileText,
  MessageSquare, ScanLine, UserPlus, Plus, Trash2, RefreshCw, Copy, Gift, Briefcase, Download, Image, ShieldCheck, Megaphone,
} from "lucide-react";
import { api, setAuthToken } from "../lib/api";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { LoyaltyTab } from "../components/admin/LoyaltyTab";
import { SitePhotosTab } from "../components/admin/SitePhotosTab";
import { IdChecksTab } from "../components/admin/IdChecksTab";
import { MultiImageField } from "../components/admin/MultiImageField";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

const TABS = [
  { id: "overview", label: "Overview", Icon: FileText },
  { id: "members", label: "Members", Icon: Users },
  { id: "checkins", label: "Check-Ins", Icon: ScanLine },
  { id: "loyalty", label: "Loyalty", Icon: Gift },
  { id: "idchecks", label: "ID Checks", Icon: ShieldCheck },
  { id: "reservations", label: "Reservations", Icon: Calendar },
  { id: "applications", label: "Applications", Icon: Users },
  { id: "bookings", label: "Bookings", Icon: Calendar },
  { id: "orders", label: "Orders", Icon: ShoppingBag },
  { id: "messages", label: "Messages", Icon: MessageSquare },
  { id: "leads", label: "Leads", Icon: Megaphone },
  { id: "careers", label: "Careers", Icon: Briefcase },
  { id: "products", label: "Products", Icon: Package },
  { id: "events", label: "Events", Icon: Calendar },
  { id: "photos", label: "Site Photos", Icon: Image },
];

const ENDPOINTS = {
  reservations: "/admin/reservations",
  applications: "/admin/applications",
  bookings: "/admin/bookings",
  orders: "/admin/orders",
  messages: "/admin/contact-messages",
  leads: "/admin/interest",
  careers: "/admin/careers",
  members: "/admin/members",
  checkins: "/admin/checkins",
  products: "/products",
  events: "/events",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try { await api.get("/auth/me"); } catch { setAuthToken(null); navigate("/admin"); }
  }, [navigate]);
  useEffect(() => { checkAuth(); }, [checkAuth]);

  const loadOverview = useCallback(async () => {
    try {
      const r = await api.get("/admin/overview");
      setOverview(r.data);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const loadTab = useCallback(async (t) => {
    if (t === "overview") return;
    setLoading(true);
    try {
      const r = await api.get(ENDPOINTS[t]);
      setData(r.data || []);
    } catch (e) {
      toast.error("Failed to load");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  const refreshAll = () => { loadOverview(); loadTab(tab); };

  const signOut = () => {
    setAuthToken(null);
    toast.success("Signed out");
    navigate("/admin");
  };

  return (
    <section className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      <header className="border-b border-[var(--twr-line)] px-6 sm:px-10 py-5 flex items-center justify-between gap-3 flex-wrap">
        <Link to="/"><Logo variant="horizontal" /></Link>
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/admin/checkin" data-testid="admin-checkin-link" className="inline-flex items-center gap-2 border border-[var(--twr-bronze)] text-[var(--twr-bronze)] hover:bg-[var(--twr-bronze)] hover:text-[#0A0A0A] px-4 py-2 text-[11px] tracking-[0.28em] uppercase transition-colors">
            <ScanLine size={13} /> Door · Check-In
          </Link>
          <Link to="/" className="text-[11px] tracking-[0.28em] uppercase text-[var(--twr-warm-white)]/55 hover:text-[var(--twr-bronze)] transition-colors">View Site</Link>
          <button onClick={signOut} data-testid="admin-signout" className="inline-flex items-center gap-2 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] px-4 py-2 text-[11px] tracking-[0.28em] uppercase transition-colors">
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </header>

      <div className="grid lg:grid-cols-12 gap-0 min-h-[calc(100vh-72px)]">
        <aside className="lg:col-span-2 border-r border-[var(--twr-line)] p-6">
          <nav className="space-y-1">
            {TABS.map(({ id, label, Icon }) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${tab === id ? "bg-[var(--twr-bronze)]/10 text-[var(--twr-bronze)] border-l-2 border-[var(--twr-bronze)]" : "text-[var(--twr-warm-white)]/70 hover:text-[var(--twr-bronze)]"}`}>
                <Icon size={14} strokeWidth={1.5} /> {label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="lg:col-span-10 p-6 sm:p-10">
          {tab === "overview" && (
            <div data-testid="admin-overview">
              <h1 className="font-serif text-4xl">Overview</h1>
              <p className="text-[var(--twr-warm-white)]/55 mt-2">A live snapshot of the club.</p>
              <ComingSoonToggle />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
                {overview && [
                  ["Members", overview.members, Users],
                  ["Check-Ins", overview.checkins, ScanLine],
                  ["Products", overview.products, Package],
                  ["Events", overview.events, Calendar],
                  ["Reservations", overview.reservations, Calendar],
                  ["Applications", overview.applications, Users],
                  ["Bookings", overview.bookings, Calendar],
                  ["Orders", overview.orders, ShoppingBag],
                  ["Newsletter", overview.newsletter, Inbox],
                  ["Messages", overview.contact_messages, MessageSquare],
                ].map(([l, v, Icon]) => (
                  <div key={l} className="surface p-6">
                    <Icon size={16} className="text-[var(--twr-bronze)]" strokeWidth={1.4} />
                    <p className="font-serif text-4xl text-[var(--twr-bronze)] mt-4">{v}</p>
                    <p className="text-[11px] tracking-[0.28em] uppercase text-[var(--twr-warm-white)]/55 mt-2">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "members" && (
            <MembersTab data={data} loading={loading} onRefresh={refreshAll} />
          )}

          {tab === "loyalty" && <LoyaltyTab />}

          {tab === "photos" && <SitePhotosTab />}

          {tab === "idchecks" && <IdChecksTab />}

          {tab === "products" && (
            <ProductsTab data={data} loading={loading} onRefresh={refreshAll} />
          )}

          {tab === "events" && (
            <EventsTab data={data} loading={loading} onRefresh={refreshAll} />
          )}

          {tab === "careers" && <CareersTab data={data} loading={loading} />}

          {["reservations", "applications", "bookings", "orders", "messages", "checkins", "leads"].includes(tab) && (
            <GenericTable tab={tab} data={data} loading={loading} />
          )}
        </main>
      </div>
    </section>
  );
}

/* ---------- Coming Soon toggle ---------- */
function ComingSoonToggle() {
  const [enabled, setEnabled] = useState(null);
  useEffect(() => {
    api.get("/settings/public").then((r) => setEnabled(r.data.coming_soon)).catch(() => setEnabled(false));
  }, []);
  const flip = async () => {
    const next = !enabled;
    try {
      await api.put("/admin/settings/coming-soon", { enabled: next });
      setEnabled(next);
      toast.success(next ? "Coming Soon page is ON — public site hidden" : "Full website is LIVE to the public");
    } catch (e) { toast.error("Could not update"); }
  };
  if (enabled === null) return null;
  return (
    <div className="surface p-5 mt-8 flex items-center justify-between gap-4 flex-wrap" data-testid="coming-soon-toggle-card">
      <div>
        <p className="eyebrow text-[var(--twr-bronze)]">Coming Soon Mode</p>
        <p className="text-sm text-[var(--twr-warm-white)]/65 mt-2">
          {enabled ? "Visitors currently see the Coming Soon page with the interest form. Admin area stays accessible." : "The full website is visible to everyone."}
        </p>
      </div>
      <button onClick={flip} data-testid="coming-soon-toggle-btn"
        className={`px-5 py-3 text-[11px] tracking-[0.28em] uppercase border transition-colors ${enabled ? "border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-[#0A0A0A]" : "border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-[#0A0A0A]"}`}>
        {enabled ? "ON — Switch to Full Site" : "OFF — Show Coming Soon"}
      </button>
    </div>
  );
}

/* ---------- Careers tab ---------- */
function CareersTab({ data, loading }) {
  const downloadResume = async (row) => {
    try {
      const r = await api.get(`/admin/careers/${row.id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = row.resume_filename || "resume";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download résumé");
    }
  };

  return (
    <div data-testid="admin-careers-tab">
      <h1 className="font-serif text-4xl">Careers</h1>
      <p className="text-[var(--twr-warm-white)]/55 mt-2">{data.length} application{data.length === 1 ? "" : "s"}</p>
      {loading ? <p className="mt-10 text-[var(--twr-warm-white)]/55">Loading…</p> : (
        <div className="mt-8 overflow-x-auto surface">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--twr-line)] text-[var(--twr-warm-white)]/55 text-[11px] uppercase tracking-[0.18em]">
              <tr>
                <th className="text-left px-5 py-4">Name</th>
                <th className="text-left px-5 py-4">Position</th>
                <th className="text-left px-5 py-4">Email</th>
                <th className="text-left px-5 py-4">Phone</th>
                <th className="text-left px-5 py-4">Message</th>
                <th className="text-left px-5 py-4">Applied</th>
                <th className="text-right px-5 py-4">Résumé</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.id} data-testid={`career-row-${row.id}`} className="border-b border-[var(--twr-line)]/60 hover:bg-[var(--twr-bronze)]/5 transition-colors">
                  <td className="px-5 py-4">{row.name}</td>
                  <td className="px-5 py-4 text-[var(--twr-warm-white)]/75">{row.position || "—"}</td>
                  <td className="px-5 py-4 text-[var(--twr-warm-white)]/75">{row.email}</td>
                  <td className="px-5 py-4 text-[var(--twr-warm-white)]/65">{row.phone || "—"}</td>
                  <td className="px-5 py-4"><div className="max-w-xs truncate text-[var(--twr-warm-white)]/65" title={row.message}>{row.message || "—"}</div></td>
                  <td className="px-5 py-4 text-[var(--twr-warm-white)]/55 text-[12px]">{new Date(row.created_at).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}</td>
                  <td className="px-5 py-4 text-right">
                    {row.resume_filename ? (
                      <button onClick={() => downloadResume(row)} data-testid={`career-resume-download-${row.id}`}
                        className="inline-flex items-center gap-2 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] transition-colors px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase">
                        <Download size={12} /> Download
                      </button>
                    ) : <span className="text-[var(--twr-warm-white)]/35 text-[12px]">None</span>}
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td className="px-5 py-10 text-center text-[var(--twr-warm-white)]/55" colSpan={7}>No applications yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- Generic table ---------- */
function GenericTable({ tab, data, loading }) {
  return (
    <div data-testid={`admin-table-${tab}`}>
      <h1 className="font-serif text-4xl capitalize">{tab.replace(/-/g, " ")}</h1>
      <p className="text-[var(--twr-warm-white)]/55 mt-2">{data.length} record{data.length === 1 ? "" : "s"}</p>
      {loading ? <p className="mt-10 text-[var(--twr-warm-white)]/55">Loading…</p> : (
        <div className="mt-8 overflow-x-auto surface">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--twr-line)] text-[var(--twr-warm-white)]/55 text-[11px] uppercase tracking-[0.18em]">
              <tr>
                {data[0] && Object.keys(data[0]).filter((k) => !["id"].includes(k)).slice(0, 7).map((k) => (
                  <th key={k} className="text-left px-5 py-4">{k.replace(/_/g, " ")}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={row.id || i} className="border-b border-[var(--twr-line)]/60 hover:bg-[var(--twr-bronze)]/5 transition-colors">
                  {Object.keys(data[0]).filter((k) => !["id"].includes(k)).slice(0, 7).map((k) => (
                    <td key={k} className="px-5 py-4 align-top">
                      <div className="max-w-xs truncate" title={String(row[k] ?? "")}>{fmt(row[k])}</div>
                    </td>
                  ))}
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td className="px-5 py-10 text-center text-[var(--twr-warm-white)]/55" colSpan={8}>Nothing yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------- Members tab with invite ---------- */
function MembersTab({ data, loading, onRefresh }) {
  return (
    <div data-testid="admin-members-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl">Members</h1>
          <p className="text-[var(--twr-warm-white)]/55 mt-2">{data.length} active</p>
        </div>
        <InviteMemberDialog onCreated={onRefresh} />
      </div>
      {loading ? <p className="mt-10 text-[var(--twr-warm-white)]/55">Loading…</p> : (
        <div className="mt-8 overflow-x-auto surface">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--twr-line)] text-[var(--twr-warm-white)]/55 text-[11px] uppercase tracking-[0.18em]">
              <tr>
                <th className="text-left px-5 py-4">Member No.</th>
                <th className="text-left px-5 py-4">Name</th>
                <th className="text-left px-5 py-4">Tier</th>
                <th className="text-left px-5 py-4">Email</th>
                <th className="text-left px-5 py-4">Points</th>
                <th className="text-left px-5 py-4">Phone</th>
                <th className="text-left px-5 py-4">Inducted</th>
                <th className="text-right px-5 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((m) => (
                <MemberRow key={m.id} m={m} onChange={onRefresh} />
              ))}
              {data.length === 0 && (
                <tr><td className="px-5 py-10 text-center text-[var(--twr-warm-white)]/55" colSpan={8}>No members yet. Invite your first.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MemberRow({ m, onChange }) {
  const regen = async () => {
    if (!window.confirm(`Reset password for ${m.full_name}? They will receive a new welcome email.`)) return;
    try {
      const r = await api.post(`/admin/members/${m.id}/regenerate-password`);
      toast.success(`New temp password: ${r.data.temp_password}`);
    } catch (e) { toast.error("Failed"); }
  };
  const del = async () => {
    if (!window.confirm(`Remove ${m.full_name} from the club?`)) return;
    try {
      await api.delete(`/admin/members/${m.id}`);
      toast.success("Member removed");
      onChange();
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <tr data-testid={`admin-member-row-${m.id}`} className="border-b border-[var(--twr-line)]/60 hover:bg-[var(--twr-bronze)]/5 transition-colors">
      <td className="px-5 py-4 font-serif text-[var(--twr-bronze)]">{m.member_no}</td>
      <td className="px-5 py-4">{m.full_name}</td>
      <td className="px-5 py-4 capitalize">{m.membership_type}</td>
      <td className="px-5 py-4 text-[var(--twr-warm-white)]/75">{m.email}</td>
      <td className="px-5 py-4 font-serif text-[var(--twr-bronze)]">{(m.points_balance || 0).toLocaleString()}</td>
      <td className="px-5 py-4 text-[var(--twr-warm-white)]/65">{m.phone || "—"}</td>
      <td className="px-5 py-4 text-[var(--twr-warm-white)]/55 text-[12px]">{new Date(m.created_at).toLocaleDateString("en-US")}</td>
      <td className="px-5 py-4 text-right">
        <div className="inline-flex gap-2">
          <button onClick={regen} data-testid={`admin-member-regen-${m.id}`} title="Reset password" className="inline-flex items-center justify-center w-8 h-8 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] transition-colors"><RefreshCw size={13}/></button>
          <button onClick={del} data-testid={`admin-member-delete-${m.id}`} title="Remove" className="inline-flex items-center justify-center w-8 h-8 border border-[var(--twr-line-strong)] hover:border-red-400 hover:text-red-400 transition-colors"><Trash2 size={13}/></button>
        </div>
      </td>
    </tr>
  );
}

function InviteMemberDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", membership_type: "executive", company: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/members", form);
      setCreated(r.data);
      toast.success(`${r.data.full_name} invited`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create");
    } finally { setBusy(false); }
  };

  const reset = () => { setForm({ full_name: "", email: "", phone: "", membership_type: "executive", company: "", notes: "" }); setCreated(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="admin-invite-member-btn" className="btn-bronze">
          <UserPlus size={14} /> Invite Member
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border border-[var(--twr-line)] text-[var(--twr-warm-white)] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-left">Invite a new member</DialogTitle>
          <DialogDescription className="text-left text-[var(--twr-muted,#A1A1AA)]">Create a member account and send welcome credentials.</DialogDescription>
        </DialogHeader>
        {!created ? (
          <form onSubmit={submit} className="space-y-4">
            <AdminInput label="Full Name" v={form.full_name} on={(v) => set("full_name", v)} required testId="invite-fullname" />
            <AdminInput label="Email" type="email" v={form.email} on={(v) => set("email", v)} required testId="invite-email" />
            <AdminInput label="Phone" v={form.phone} on={(v) => set("phone", v)} testId="invite-phone" />
            <AdminInput label="Company" v={form.company} on={(v) => set("company", v)} testId="invite-company" />
            <label className="block">
              <span className="eyebrow">Tier</span>
              <select data-testid="invite-tier" value={form.membership_type} onChange={(e) => set("membership_type", e.target.value)}
                className="mt-3 w-full bg-[#0A0A0A] border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm">
                <option value="founders">Founders 100</option>
                <option value="executive">Executive</option>
                <option value="corporate">Corporate</option>
              </select>
            </label>
            <DialogFooter className="pt-2">
              <button disabled={busy} data-testid="invite-submit" className="btn-bronze">{busy ? "Inviting…" : "Send Invitation"}</button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4" data-testid="invite-created">
            <p className="eyebrow text-[var(--twr-bronze)]">Member created · Welcome email sent</p>
            <h3 className="font-serif text-2xl">{created.full_name}</h3>
            <div className="surface p-5 space-y-3">
              <Row l="Member No." v={created.member_no} />
              <Row l="Email" v={created.email} />
              <div>
                <span className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55">Temporary Password</span>
                <div className="flex items-center gap-2 mt-2">
                  <code className="font-mono text-[var(--twr-bronze)] text-lg">{created.temp_password}</code>
                  <button onClick={() => { navigator.clipboard.writeText(created.temp_password); toast.success("Copied"); }} className="inline-flex items-center justify-center w-7 h-7 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)]">
                    <Copy size={12}/>
                  </button>
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="btn-ghost w-full">Close</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Products tab ---------- */
function ProductsTab({ data, loading, onRefresh }) {
  return (
    <div data-testid="admin-products-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl">Products</h1>
          <p className="text-[var(--twr-warm-white)]/55 mt-2">{data.length} listed</p>
        </div>
        <ProductDialog onSaved={onRefresh} />
      </div>
      {loading ? <p className="mt-10 text-[var(--twr-warm-white)]/55">Loading…</p> : (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((p) => (
            <div key={p.id} data-testid={`admin-product-${p.id}`} className="surface p-4 flex gap-4">
              <img src={p.image} alt={p.name} className="w-20 h-20 object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-serif truncate">{p.name}</p>
                <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55 mt-1">{p.category}</p>
                <p className="font-serif text-[var(--twr-bronze)] mt-1">${(p.price ?? 0).toFixed(2)}</p>
              </div>
              <div className="flex flex-col gap-1">
                <ProductDialog product={p} onSaved={onRefresh} />
                <DeleteBtn testId={`product-delete-${p.id}`} onConfirm={async () => { await api.delete(`/admin/products/${p.id}`); toast.success("Deleted"); onRefresh(); }} />
              </div>
            </div>
          ))}
          {data.length === 0 && <p className="text-[var(--twr-warm-white)]/55 col-span-full text-center py-10">No products yet.</p>}
        </div>
      )}
    </div>
  );
}

function ProductDialog({ product, onSaved }) {
  const editing = !!product;
  const empty = { name: "", category: "cigars", price: 0, description: "", long_description: "", image: "", images: [], featured: false, in_stock: true, origin: "", strength: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (open && editing) setForm({ ...empty, ...product }); if (open && !editing) setForm(empty); }, [open]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 };
      if (editing) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Updated" : "Created");
      onSaved(); setOpen(false);
    } catch (err) {
      toast.error("Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={editing ? `product-edit-${product.id}` : "admin-new-product-btn"} className={editing ? "inline-flex items-center justify-center w-7 h-7 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)]" : "btn-bronze"}>
          {editing ? <Plus size={12}/> : <><Plus size={14}/> New Product</>}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border border-[var(--twr-line)] text-[var(--twr-warm-white)] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-left">{editing ? "Edit product" : "New product"}</DialogTitle>
          <DialogDescription className="text-left text-[var(--twr-muted,#A1A1AA)]">{editing ? "Update the details of this product." : "Add a new product to the shop."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <AdminInput label="Name" v={form.name} on={(v) => set("name", v)} required />
            <label className="block">
              <span className="eyebrow">Category</span>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-3 w-full bg-[#0A0A0A] border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm">
                {["cigars", "accessories", "apparel", "spirits", "gift-cards"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <AdminInput label="Price (USD)" type="number" v={form.price} on={(v) => set("price", v)} required step="0.01" />
            <MultiImageField
              label="Photos (first = main; extras show in a carousel)"
              value={[form.image, ...(form.images || [])].filter(Boolean)}
              onChange={(arr) => setForm((f) => ({ ...f, image: arr[0] || "", images: arr.slice(1) }))}
              testId="admin-image-field"
            />
            <AdminInput label="Origin" v={form.origin || ""} on={(v) => set("origin", v)} />
            <AdminInput label="Strength" v={form.strength || ""} on={(v) => set("strength", v)} />
          </div>
          <AdminTextarea label="Short Description" v={form.description} on={(v) => set("description", v)} required />
          <AdminTextarea label="Long Description" v={form.long_description || ""} on={(v) => set("long_description", v)} rows={4} />
          <div className="flex items-center gap-6 pt-2">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[var(--twr-bronze)]" />
              <span className="text-[11px] tracking-[0.22em] uppercase">Featured</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.in_stock} onChange={(e) => set("in_stock", e.target.checked)} className="accent-[var(--twr-bronze)]" />
              <span className="text-[11px] tracking-[0.22em] uppercase">In Stock</span>
            </label>
          </div>
          <DialogFooter><button disabled={busy} className="btn-bronze">{busy ? "Saving…" : (editing ? "Save" : "Create")}</button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Events tab ---------- */
function EventsTab({ data, loading, onRefresh }) {
  return (
    <div data-testid="admin-events-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl">Events</h1>
          <p className="text-[var(--twr-warm-white)]/55 mt-2">{data.length} scheduled</p>
        </div>
        <EventDialog onSaved={onRefresh} />
      </div>
      {loading ? <p className="mt-10 text-[var(--twr-warm-white)]/55">Loading…</p> : (
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {data.map((ev) => (
            <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="surface p-5 flex gap-4">
              <img src={ev.image} alt={ev.title} className="w-24 h-20 object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-serif truncate">{ev.title}</p>
                <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55 mt-1">{ev.category}</p>
                <p className="text-[var(--twr-bronze)] text-[12px] mt-1">{ev.date ? new Date(ev.date).toLocaleDateString() : "—"}</p>
              </div>
              <div className="flex flex-col gap-1">
                <EventDialog event={ev} onSaved={onRefresh} />
                <DeleteBtn testId={`event-delete-${ev.id}`} onConfirm={async () => { await api.delete(`/admin/events/${ev.id}`); toast.success("Deleted"); onRefresh(); }} />
              </div>
            </div>
          ))}
          {data.length === 0 && <p className="text-[var(--twr-warm-white)]/55 col-span-full text-center py-10">No events yet.</p>}
        </div>
      )}
    </div>
  );
}

function EventDialog({ event, onSaved }) {
  const editing = !!event;
  const empty = { title: "", category: "networking", description: "", date: "", location: "The Win Room • Lake Nona, Orlando", image: "", images: [], price: 0, seats_total: 50, seats_remaining: 50, members_only: false };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open && editing) setForm({ ...empty, ...event, date: event.date ? event.date.slice(0, 16) : "" });
    if (open && !editing) setForm(empty);
  }, [open]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        seats_total: parseInt(form.seats_total) || 0,
        seats_remaining: parseInt(form.seats_remaining) || 0,
        date: form.date ? new Date(form.date).toISOString() : "",
      };
      if (editing) await api.put(`/admin/events/${event.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success(editing ? "Updated" : "Created");
      onSaved(); setOpen(false);
    } catch (err) { toast.error("Failed"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={editing ? `event-edit-${event.id}` : "admin-new-event-btn"} className={editing ? "inline-flex items-center justify-center w-7 h-7 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)]" : "btn-bronze"}>
          {editing ? <Plus size={12}/> : <><Plus size={14}/> New Event</>}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border border-[var(--twr-line)] text-[var(--twr-warm-white)] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-left">{editing ? "Edit event" : "New event"}</DialogTitle>
          <DialogDescription className="text-left text-[var(--twr-muted,#A1A1AA)]">{editing ? "Update the details of this event." : "Create a new event for the calendar."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <AdminInput label="Title" v={form.title} on={(v) => set("title", v)} required />
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="eyebrow">Category</span>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-3 w-full bg-[#0A0A0A] border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm">
                {["whiskey-tasting","cigar-tasting","networking","live-music","speaker","poker","golf","private-dinner"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <AdminInput label="Date & Time" type="datetime-local" v={form.date} on={(v) => set("date", v)} required />
            <MultiImageField
              label="Photos (first = main; extras show in a carousel)"
              value={[form.image, ...(form.images || [])].filter(Boolean)}
              onChange={(arr) => setForm((f) => ({ ...f, image: arr[0] || "", images: arr.slice(1) }))}
              testId="admin-image-field"
            />
            <AdminInput label="Location" v={form.location} on={(v) => set("location", v)} required />
            <AdminInput label="Price (USD)" type="number" v={form.price} on={(v) => set("price", v)} step="0.01" />
            <AdminInput label="Seats Total" type="number" v={form.seats_total} on={(v) => set("seats_total", v)} required />
            <AdminInput label="Seats Remaining" type="number" v={form.seats_remaining} on={(v) => set("seats_remaining", v)} required />
            <label className="inline-flex items-center gap-2 mt-9 cursor-pointer">
              <input type="checkbox" checked={!!form.members_only} onChange={(e) => set("members_only", e.target.checked)} className="accent-[var(--twr-bronze)]" />
              <span className="text-[11px] tracking-[0.22em] uppercase">Members only</span>
            </label>
          </div>
          <AdminTextarea label="Description" v={form.description} on={(v) => set("description", v)} required />
          <DialogFooter><button disabled={busy} className="btn-bronze">{busy ? "Saving…" : (editing ? "Save" : "Create")}</button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Shared bits ---------- */
function AdminInput({ label, v, on, type = "text", required, step, testId }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}{required && " *"}</span>
      <input data-testid={testId} type={type} step={step} value={v} onChange={(e) => on(e.target.value)} required={required}
        className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm transition-colors" />
    </label>
  );
}
function AdminTextarea({ label, v, on, required, rows = 3 }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}{required && " *"}</span>
      <textarea value={v} onChange={(e) => on(e.target.value)} required={required} rows={rows}
        className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-3 py-3 text-sm transition-colors" />
    </label>
  );
}
function DeleteBtn({ onConfirm, testId }) {
  return (
    <button data-testid={testId} onClick={async () => { if (window.confirm("Delete this?")) try { await onConfirm(); } catch (e) { toast.error("Failed"); } }}
      className="inline-flex items-center justify-center w-7 h-7 border border-[var(--twr-line-strong)] hover:border-red-400 hover:text-red-400 transition-colors">
      <Trash2 size={12}/>
    </button>
  );
}
function Row({ l, v }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55">{l}</span>
      <span className="text-sm">{v}</span>
    </div>
  );
}
function fmt(v) {
  if (v == null) return "—";
  if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`;
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  if (s.length > 60) return s.slice(0, 60) + "…";
  return s;
}
